"use client"

import { createContext, useContext, useMemo, useEffect, ReactNode } from "react"
import { createBrowserClient } from "@supabase/ssr"
import type { SupabaseClient } from "@supabase/supabase-js"
import posthog from "posthog-js"

const SupabaseContext = createContext<SupabaseClient | null>(null)

// Shared singleton client instance - ensures only one client exists across the app
// 
// Why singleton for browser clients:
// - Browser clients are stateless and automatically use browser storage/cookies
// - Creating multiple instances is wasteful and can cause inconsistencies
// - Unlike server clients (which need per-request cookie context), browser clients are safe to share
//
// When you might need multiple instances:
// - Multiple Supabase projects (different URLs/keys)
// - Testing isolation (mocking)
// - For a single project, singleton is the recommended pattern
let sharedClient: SupabaseClient | null = null

/**
 * Creates or returns the shared Supabase client instance.
 * Returns null if env vars are missing so callers can show a clear UI state.
 */
function getOrCreateSharedClient(): SupabaseClient | null {
  // If client already exists, return it immediately
  if (sharedClient) {
    return sharedClient
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  if (!url || !key) {
    // Missing configuration — let the caller decide how to render
    console.error("DB environment variables are required but not available")
    return null
  }

  // Create client synchronously (createBrowserClient is synchronous)
  sharedClient = createBrowserClient(url, key)
  return sharedClient
}

export function SupabaseProvider({ children }: { children: ReactNode }) {
  const supabase = useMemo(() => {
    // During static generation (no window), return null
    // The actual client will be created when accessed via useSupabase
    if (typeof window === "undefined") {
      return null
    }

    // Try to create client if env vars are available
    // If not available during build, return null and create lazily
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL
    const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

    if (url && key) {
      try {
        // Use shared client to ensure consistency
        return getOrCreateSharedClient()
      } catch (error) {
        // If createBrowserClient fails (e.g., during build), return null
        // Client will be created lazily when accessed
        console.warn("DB client creation deferred:", error)
        return null
      }
    }

    // Env vars not available - return null, will be created lazily
    return null
  }, [])

  // Keep PostHog identity in sync with the Supabase session.
  //
  // The login/signup forms call posthog.identify() on a fresh sign-in, but a
  // returning visitor whose session is restored from cookies never hits those
  // handlers and would otherwise stay on an anonymous distinct ID. Subscribing
  // here covers every entry point: onAuthStateChange fires an INITIAL_SESSION
  // event right after we subscribe, carrying the restored session (or null for
  // genuinely anonymous visitors, who are correctly left un-identified).
  useEffect(() => {
    if (!supabase) return

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        posthog.reset()
      } else if (session?.user) {
        // SIGNED_IN, TOKEN_REFRESHED, USER_UPDATED, INITIAL_SESSION
        // signed_up_at is $set_once: it lets surveys (e.g. NPS) target people
        // by account age, and it never changes after the first identify.
        posthog.identify(
          session.user.id,
          { email: session.user.email },
          { signed_up_at: session.user.created_at }
        )
      }
    })

    return () => subscription.unsubscribe()
  }, [supabase])

  return <SupabaseContext.Provider value={supabase}>{children}</SupabaseContext.Provider>
}

export function useSupabase(): SupabaseClient {
  const context = useContext(SupabaseContext)
  
  // If context is null (during build/SSR or if env vars weren't available), try to create shared client
  if (!context) {
    const client = getOrCreateSharedClient()
    if (!client) {
      throw new Error(
        "DB is not configured properly. Env vars are missing."
      )
    }
    return client
  }
  
  // Ensure context client is also the shared client (for consistency)
  // If they differ, prefer the shared one to avoid multiple instances
  if (sharedClient && context !== sharedClient) {
    // This should rarely happen, but ensures consistency
    return sharedClient
  }
  
  // Update shared client reference if it's not set
  if (!sharedClient) {
    sharedClient = context
  }
  
  return context
}

